import React from 'react';

const appearances = {
  'examiner-01': { skin: '#e4ae87', hair: '#50362d', jacket: '#24435d', shirt: '#f4f2eb', background: '#e5eef4', hairStyle: 'short', glasses: true },
  'examiner-02': { skin: '#8e573d', hair: '#251d1b', jacket: '#275957', shirt: '#f2e4cf', background: '#e4f1eb', hairStyle: 'curly' },
  'examiner-03': { skin: '#ecc8aa', hair: '#a3a8af', jacket: '#51466c', shirt: '#fcf8ec', background: '#eeebf6', hairStyle: 'long' },
  'examiner-04': { skin: '#bd875e', hair: '#28282d', jacket: '#4a5364', shirt: '#e8f0f1', background: '#e9eef0', hairStyle: 'swept', glasses: true }
};

export const hasExaminerAppearance = profile => !!appearances[profile?.avatarAssetKey] && String(profile?.avatarVersion) === '1';

const ExaminerAvatar = ({ profile, level = 0, activity = 'idle', compact = false }) => {
  const appearance = appearances[profile?.avatarAssetKey];
  if (!hasExaminerAppearance(profile)) return <div className="examiner-avatar-unavailable" role="status">
    {profile ? `Appearance unavailable for ${profile.name} (${profile.avatarAssetKey}, version ${profile.avatarVersion}).` : 'Select an examiner to see their appearance.'}
  </div>;
  const speaking = level > 0.025;
  const mouth = speaking ? 2 + Math.min(1, Math.max(0, level)) * 13 : 1;
  const state = speaking ? 'speaking' : activity;
  return <figure className={`examiner-avatar ${compact ? 'examiner-avatar-compact' : ''}`} data-activity={state}>
    <svg viewBox="0 0 240 250" role="img" aria-label={`${profile.name}, animated fictional examiner`} style={{ background: appearance.background }}>
      <circle cx="120" cy="98" r="80" fill="white" opacity=".5" />
      <path d="M27 250v-28c0-40 38-65 93-65s93 25 93 65v28" fill={appearance.jacket} />
      <path d="m91 178 29 54 29-54" fill={appearance.shirt} />
      <path d="M104 148v29l16 19 16-19v-29" fill={appearance.skin} />
      <g className="examiner-avatar-head">
        {appearance.hairStyle === 'long' && <path d="M62 104C46 18 186 10 180 104l6 87-43-14-57 4-32 8Z" fill={appearance.hair} />}
        <ellipse cx="70" cy="108" rx="9" ry="16" fill={appearance.skin} />
        <ellipse cx="170" cy="108" rx="9" ry="16" fill={appearance.skin} />
        <path d="M70 81c0-61 100-61 100 0v38c0 37-24 55-50 55s-50-18-50-55Z" fill={appearance.skin} />
        {appearance.hairStyle === 'short' && <path d="M68 94c-9-46 18-64 55-64 41 0 54 25 48 62l-15-31c-24 10-49 8-70-1Z" fill={appearance.hair} />}
        {appearance.hairStyle === 'curly' && <g fill={appearance.hair}>{[[76,65],[89,46],[111,39],[135,40],[155,50],[168,70]].map(([cx, cy]) => <circle key={cx} cx={cx} cy={cy} r="18" />)}</g>}
        {appearance.hairStyle === 'long' && <path d="M66 92c-3-57 105-74 109 1l-27-37-20 8-40 7Z" fill={appearance.hair} />}
        {appearance.hairStyle === 'swept' && <path d="M68 94c-19-57 31-78 91-54l14 47-20-25c-32 21-57 20-69 17l-10 22Z" fill={appearance.hair} />}
        <path d="m86 93 20-2m28 0 20 2" fill="none" stroke={appearance.hair} strokeWidth="4" strokeLinecap="round" />
        <g className="examiner-avatar-eyes" fill="#252b32">
          <ellipse cx="98" cy="107" rx="4" ry="4.5" />
          <ellipse cx="143" cy="107" rx="4" ry="4.5" />
        </g>
        {appearance.glasses && <g stroke="#38404c" fill="none" strokeWidth="2.5"><rect x="80" y="98" width="33" height="20" rx="7" /><rect x="128" y="98" width="33" height="20" rx="7" /><path d="M113 105h15" /></g>}
        <path d="m119 109-4 18h9" fill="none" stroke="#956953" strokeWidth="2" strokeLinecap="round" />
        <ellipse data-testid="examiner-mouth" cx="120" cy="143" rx={speaking ? 12 : 13} ry={mouth} fill="#613c36" />
        {speaking && <path d="M111 141h18" stroke="#fff3e5" strokeWidth="2" />}
      </g>
    </svg>
    {!compact && <figcaption><strong>{profile.name}</strong><span>{state === 'speaking' ? 'Speaking' : ({ listening: 'Listening', thinking: 'Thinking', paused: 'Paused', completed: 'Completed', connecting: 'Connecting' }[state] || 'Ready')}</span></figcaption>}
  </figure>;
};

export default ExaminerAvatar;
